import type { TFunction } from "i18next";
import type { WalletTransaction } from "@/types";

export type WalletMovementPresentation = {
  title: string;
  badgeLabel: string;
  movementKind: WalletTransaction["movement_kind"];
};

export function resolveWalletMovementPresentation(
  transaction: WalletTransaction,
  t: TFunction<"components">,
): WalletMovementPresentation {
  let key = transaction.movement_key ?? inferMovementKeyFromLegacy(transaction);
  key = normalizeHostMovementKey(key, transaction);
  const kind =
    transaction.movement_kind ??
    (key === "settlement_sent"
      ? "settlement_transfer"
      : key === "refund"
        ? "refund"
        : transaction.type === "CREDIT"
          ? "income"
          : "expense");
  const ctx = transaction.display_context ?? {};
  const reference = ctx.reference?.trim();

  const movementKey = WALLET_MOVEMENT_KEYS.has(key) ? key : "unknown";
  let title = t(`walletMovements.${movementKey}.title`, {
    defaultValue: t("walletMovements.unknown.title"),
  });
  if (
    reference &&
    (movementKey === "settlement_sent" || movementKey === "settlement_received")
  ) {
    title = t(`walletMovements.${movementKey}.titleWithReference`, {
      reference,
      defaultValue: title,
    });
  }

  const badgeKey =
    kind === "settlement_transfer"
      ? "settlement"
      : kind === "refund"
        ? "refund"
        : transaction.type === "CREDIT"
          ? "income"
          : "expense";

  return {
    title,
    badgeLabel: t(`walletMovements.badges.${badgeKey}`),
    movementKind: kind,
  };
}

function normalizeHostMovementKey(
  key: string,
  transaction: WalletTransaction,
): string {
  if (transaction.entry_role !== "HOST" || transaction.type !== "CREDIT") {
    return key;
  }
  if (
    key === "host_commission" ||
    key === "checkout_payment" ||
    key === "restaurant_fee" ||
    key === "host_payout"
  ) {
    return "host_commission";
  }
  if (
    transaction.economic_event_type === "CHECKOUT_CAPTURED" ||
    transaction.economic_event_type === "SELLER_FEE_FROM_RECEIPT"
  ) {
    return "host_commission";
  }
  return key;
}

function inferMovementKeyFromLegacy(transaction: WalletTransaction): string {
  const lower = transaction.description.toLowerCase();
  if (lower.includes("manual bank transfer sent") || lower.startsWith("internal fee"))
    return "settlement_sent";
  if (lower.includes("manual bank transfer received")) return "settlement_received";
  if (lower.includes("host payout")) return "host_commission";
  if (lower.includes("payment for checkout")) return "checkout_payment";
  if (lower.includes("restaurant fee")) return "restaurant_fee";
  if (lower.includes("refund")) return "refund";
  return "unknown";
}

const WALLET_MOVEMENT_KEYS = new Set([
  "checkout_payment",
  "restaurant_fee",
  "refund",
  "settlement_sent",
  "settlement_received",
  "host_payout",
  "host_commission",
  "sales_agent_commission",
  "internal_fee",
  "unknown",
]);

export function walletMovementSearchText(
  transaction: WalletTransaction,
  t: TFunction<"components">,
): string {
  const { title, badgeLabel } = resolveWalletMovementPresentation(transaction, t);
  const parts = [
    title,
    badgeLabel,
    transaction.description,
    transaction.display_context?.reference,
    transaction.display_context?.note,
  ];
  return parts.filter(Boolean).join(" ").toLowerCase();
}
